import React from "react";
import { Link } from "react-router-dom";
import useSEO from "../lib/useSEO";
import { Check, X, Star, Zap, Crown, ArrowRight, Mail, Phone, Image as ImageIcon, Eye, Users } from "lucide-react";

const FEATURES = [
    { label: "Get listed in your county + practice area", basic: true, pro: true, elite: true },
    { label: "Phone number on profile", basic: true, pro: true, elite: true, icon: Phone },
    { label: "Photos on profile", basic: "1", pro: "Up to 5", elite: "Unlimited", icon: ImageIcon },
    { label: "Profile bio length", basic: "Short", pro: "Extended", elite: "Premium" },
    { label: "Search result position", basic: "Standard", pro: "Boosted", elite: "Top of page", icon: Eye },
    { label: "Featured badge", basic: false, pro: true, elite: true, icon: Star },
    { label: "Lead contact form on profile", basic: false, pro: true, elite: true, icon: Mail },
    { label: "Receives consumer contact submissions", basic: false, pro: true, elite: true },
    { label: "Premium slot (limited per county)", basic: false, pro: "Pro slots", elite: "Elite slots first" },
    { label: "Display order priority", basic: "After Pro/Elite", pro: "After Elite", elite: "First in county/category" },
    { label: "Highest visibility tier", basic: false, pro: false, elite: true, icon: Crown },
];

function FeatureCell({ value }) {
    if (value === true) return <Check className="h-4 w-4 text-primary mx-auto" />;
    if (value === false) return <X className="h-4 w-4 text-muted-foreground/40 mx-auto" />;
    return <span className="text-sm">{value}</span>;
}

function PlanCard({ tier, name, tagline, icon: Icon, accent, ctaLabel, recommended }) {
    return (
        <div className={`ll-card relative ${recommended ? "ring-2 ring-primary" : ""}`} data-testid={`plan-card-${tier}`}>
            {recommended && (
                <span className="absolute -top-3 left-1/2 -translate-x-1/2 bg-primary text-primary-foreground text-[10px] font-bold uppercase tracking-wider px-3 py-1 rounded-full">
                    Most popular
                </span>
            )}
            <div className="flex items-center gap-2 mb-2">
                <Icon className={`h-5 w-5 ${accent}`} />
                <h3 className="font-serif text-2xl font-bold">{name}</h3>
            </div>
            <p className="text-sm text-muted-foreground mb-4">{tagline}</p>
            <div className="text-xs text-muted-foreground mb-5">
                Pricing varies by county demand band. See real-time quote in your dashboard after sign-in.
            </div>
            <Link to="/attorney/auth?mode=register"
                  className={`inline-flex items-center justify-center gap-2 w-full rounded-full px-5 py-2.5 text-sm font-semibold transition ${
                      recommended ? "bg-primary text-primary-foreground hover:bg-primary/90" : "border border-primary/30 text-primary hover:bg-primary/5"
                  }`}
                  data-testid={`plan-cta-${tier}`}>
                {ctaLabel} <ArrowRight className="h-4 w-4" />
            </Link>
        </div>
    );
}

export default function PlanComparisonPage() {
    useSEO({
        title: "Attorney Plans — Basic, Pro, Elite",
        description:
            "Three plans for attorneys. Basic = get listed. Pro = better visibility + lead contact submissions. Elite = top placement + leads first. County-by-county pricing.",
        path: "/for-attorneys/pricing",
    });

    return (
        <section className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 py-12 lg:py-16">
            <div className="text-center mb-12">
                <span className="ll-pill mb-3">For Attorneys</span>
                <h1 className="font-serif text-4xl sm:text-5xl lg:text-6xl font-bold tracking-tight mb-4" data-testid="plans-headline">
                    Three plans.<br />
                    <span className="text-primary">One clear upgrade path.</span>
                </h1>
                <p className="text-base text-muted-foreground max-w-2xl mx-auto">
                    Basic gets you listed. Pro adds visibility and lead capture. Elite puts you at the top of every consumer
                    result in your county and practice area — and routes leads to you first.
                </p>
            </div>

            <div className="grid md:grid-cols-3 gap-6 mb-14">
                <PlanCard tier="basic" name="Basic" tagline="Get listed in your counties. Standard placement. Phone-only contact." icon={Users} accent="text-muted-foreground" ctaLabel="Start with Basic" />
                <PlanCard tier="pro" name="Pro" tagline="Boosted placement, multiple photos, longer bio, and the lead contact form so consumers can reach you directly." icon={Zap} accent="text-primary" ctaLabel="Upgrade to Pro" recommended />
                <PlanCard tier="elite" name="Elite" tagline="Top of every consumer search in your county. Unlimited photos. Leads delivered first. Limited slots." icon={Crown} accent="text-primary" ctaLabel="Apply for Elite" />
            </div>

            <h2 className="font-serif text-2xl font-bold mb-4">Feature comparison</h2>
            <div className="ll-card overflow-x-auto p-0">
                <table className="w-full text-sm" data-testid="plan-comparison-table">
                    <thead>
                        <tr className="border-b border-border bg-muted/30">
                            <th className="ll-label text-left py-3 px-4 w-1/2">Feature</th>
                            <th className="ll-label text-center py-3 px-4">Basic</th>
                            <th className="ll-label text-center py-3 px-4">Pro</th>
                            <th className="ll-label text-center py-3 px-4">Elite</th>
                        </tr>
                    </thead>
                    <tbody>
                        {FEATURES.map((f, i) => (
                            <tr key={i} className="border-b border-border last:border-0">
                                <td className="py-3 px-4 flex items-center gap-2">
                                    {f.icon && <f.icon className="h-3.5 w-3.5 text-muted-foreground" />}
                                    {f.label}
                                </td>
                                <td className="py-3 px-4 text-center"><FeatureCell value={f.basic} /></td>
                                <td className="py-3 px-4 text-center"><FeatureCell value={f.pro} /></td>
                                <td className="py-3 px-4 text-center"><FeatureCell value={f.elite} /></td>
                            </tr>
                        ))}
                    </tbody>
                </table>
            </div>

            <div className="mt-12 rounded-lg bg-accent/30 border border-border p-6 sm:p-8">
                <h3 className="font-serif text-xl font-bold mb-2">How the lead form works</h3>
                <p className="text-sm text-muted-foreground leading-relaxed">
                    Consumers visiting your profile on Pro or Elite see a short, optional form: name, email, phone (optional),
                    a few sentences about their situation, preferred contact method, and an explicit consent checkbox. You see
                    those submissions in your dashboard's Leads inbox — sorted New → Contacted → Archived. Basic listings show
                    your phone number; consumers call you directly.
                </p>
            </div>

            <div className="mt-12 text-center">
                <Link to="/attorney/auth?mode=register"
                      className="inline-flex items-center gap-2 rounded-full bg-primary text-primary-foreground px-7 py-3.5 font-semibold hover:bg-primary/90 transition-colors"
                      data-testid="plans-bottom-cta">
                    Create your attorney account <ArrowRight className="h-4 w-4" />
                </Link>
            </div>
        </section>
    );
}
